import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createEditor, Editor, Selection, Transforms } from 'domains/reporter/RichTextEditor/core';
import type { SlateContent } from 'domains/reporter/RichTextEditor';
import type { EditorPlugin } from 'domains/reporter/RichTextEditor/types';
import { PluginsProvider } from 'domains/reporter/RichTextEditor/hooks/usePlugins';
import { EnhancedSlate } from 'domains/reporter/RichTextEditor/ui/EnhancedSlate';
import { SlateProps } from 'slate-react';
import { UnifiedEditorElementProvider } from 'domains/reporter/RichTextEditor/ui/UnifiedEditorElementContext';

type SlateContextState = Omit<SlateProps, 'children'>;

type SlateContextBag = {
  registerChangeHandlers: (handlers: {
    onValueChange?: (value: SlateContent) => void;
    onSelectionChange?: (selection: Selection) => void;
  }) => void;
  resetEditorWithContent: (initialValue: SlateContent) => void;
};

const SlateContext = createContext<SlateContextBag | null>(null);

const resetEditorContent = (editor: Editor, initialValue: SlateContent) => {
  Editor.withoutNormalizing(editor, () => {
    for (let i = editor.children.length - 1; i >= 0; i -= 1) {
      Transforms.removeNodes(editor, { at: [i] });
    }

    if (initialValue.length > 0) {
      Transforms.insertNodes(editor, initialValue, { at: [0] });
      Transforms.select(editor, Editor.start(editor, []));
    }
  });

  if ('history' in editor) {
    (
      editor as Editor & {
        history?: { undos: unknown[]; redos: unknown[] };
      }
    ).history = { undos: [], redos: [] };
  }
};

export function SlateContextProvider({
  children,
  initialValue = defaultInitialValue(),
  plugins = [],
  editor: editorOverride,
}: {
  children: React.ReactNode;
  initialValue?: SlateContent;
  plugins?: EditorPlugin[];
  editor?: Editor;
}) {
  const [contextValue, setContextValue] = useState<SlateContextState>(() =>
    getDefaultValue(initialValue, editorOverride)
  );

  const [unifiedEditorElement, setUnifiedEditorElement] = useState<HTMLElement | null>(null);

  const registerChangeHandlers = useCallback(
    ({
      onValueChange,
      onSelectionChange,
    }: {
      onValueChange?: (value: SlateContent) => void;
      onSelectionChange?: (selection: Selection) => void;
    }) => {
      setContextValue((value) => ({
        ...value,
        onValueChange: onValueChange ?? value.onValueChange,
        onSelectionChange: onSelectionChange ?? value.onSelectionChange,
      }));
    },
    []
  );

  const resetEditorWithContent = useCallback((initialValue: SlateContent) => {
    setContextValue((value) => {
      // Reset the editor content instead of using a fresh editor instance to avoid visual issues with remounting
      resetEditorContent(value.editor, initialValue);
      return {
        ...value,
        initialValue: initialValue,
      };
    });
  }, []);

  const context = useMemo(
    () => ({
      registerChangeHandlers,
      resetEditorWithContent,
    }),
    [registerChangeHandlers, resetEditorWithContent]
  );

  return (
    <SlateContext.Provider value={context}>
      <UnifiedEditorElementProvider
        value={{ element: unifiedEditorElement, setElement: setUnifiedEditorElement }}
      >
        <PluginsProvider plugins={plugins}>
          <EnhancedSlate
            editor={contextValue.editor}
            initialValue={contextValue.initialValue}
            onSelectionChange={contextValue.onSelectionChange}
            onValueChange={contextValue.onValueChange}
          >
            {children}
          </EnhancedSlate>
        </PluginsProvider>
      </UnifiedEditorElementProvider>
    </SlateContext.Provider>
  );
}

export const useSlateContext = (): SlateContextBag => {
  const value = useContext(SlateContext);
  if (!value) {
    throw new Error('useSlateContext must be used within a SlateContextProvider.');
  }
  return value;
};

const getDefaultValue = (
  initialValue: SlateContent,
  editorOverride?: Editor
): SlateContextState => ({
  editor: editorOverride || createEditor(),
  initialValue: editorOverride?.children || initialValue,
  onSelectionChange: () => {},
  onValueChange: () => {},
});

const defaultInitialValue = () => [{ type: 'paragraph', children: [{ text: '' }] }];
